import { clamp } from "./geometry.js";
import { hashString, SeededRandom } from "./prng.js";
import type {
  DailyCourt,
  DailyCourtOptions,
  DefenderId,
  Difficulty,
  OffensivePlayerSpec,
  PlayerId,
  PossessionSpec,
  RouteKind,
  Vec2,
} from "./types.js";
import { POSSESSION_COUNT } from "./types.js";

const DEFAULT_LAUNCH_EPOCH = "2026-08-18";
const V2_START_UTC_DATE = "2026-08-19";

interface Template {
  label: string;
  difficulty: Difficulty;
  ballhandler: PlayerId;
  points: readonly [Vec2, Vec2, Vec2];
  targets: readonly [Vec2, Vec2, Vec2];
  routes: readonly [RouteKind, RouteKind, RouteKind];
  defenderGap: readonly [number, number, number];
  reactions: readonly [number, number, number];
  noviceFloor?: boolean;
  climax?: boolean;
  secondPassRoute?: {
    periods: readonly [number, number, number];
    phases: readonly [number, number, number];
  };
}

const TEMPLATES: readonly Template[] = [
  {
    label: "First daylight",
    difficulty: "flow",
    ballhandler: "o0",
    points: [{ x: 0.5, y: 0.55 }, { x: 0.18, y: 0.48 }, { x: 0.82, y: 0.48 }],
    targets: [{ x: 0.52, y: 0.5 }, { x: 0.12, y: 0.31 }, { x: 0.78, y: 0.38 }],
    routes: ["hold", "lift", "drift"],
    defenderGap: [0.28, 0.17, 0.14],
    reactions: [14, 20, 17],
    noviceFloor: true,
    secondPassRoute: { periods: [160, 193, 206], phases: [58, 31, 35] },
  },
  {
    label: "Corner lift",
    difficulty: "mixed",
    ballhandler: "o1",
    points: [{ x: 0.47, y: 0.66 }, { x: 0.46, y: 0.52 }, { x: 0.79, y: 0.48 }],
    targets: [{ x: 0.7, y: 0.52 }, { x: 0.35, y: 0.47 }, { x: 0.89, y: 0.25 }],
    routes: ["exchange", "drift", "lift"],
    defenderGap: [0.15, 0.3, 0.13],
    reactions: [17, 14, 23],
  },
  {
    label: "Paint exchange",
    difficulty: "mixed",
    ballhandler: "o2",
    points: [{ x: 0.22, y: 0.45 }, { x: 0.52, y: 0.71 }, { x: 0.68, y: 0.48 }],
    targets: [{ x: 0.47, y: 0.34 }, { x: 0.75, y: 0.5 }, { x: 0.88, y: 0.25 }],
    routes: ["shallow-cut", "exchange", "lift"],
    defenderGap: [0.18, 0.15, 0.3],
    reactions: [19, 16, 20],
  },
  {
    label: "Weak-side drift",
    difficulty: "flow",
    ballhandler: "o0",
    points: [{ x: 0.45, y: 0.58 }, { x: 0.15, y: 0.28 }, { x: 0.78, y: 0.55 }],
    targets: [{ x: 0.55, y: 0.54 }, { x: 0.11, y: 0.22 }, { x: 0.64, y: 0.4 }],
    routes: ["drift", "hold", "shallow-cut"],
    defenderGap: [0.33, 0.14, 0.18],
    reactions: [13, 22, 18],
    secondPassRoute: { periods: [182, 218, 165], phases: [62, 33, 30] },
  },
  {
    label: "Slot switch",
    difficulty: "scramble",
    ballhandler: "o1",
    points: [{ x: 0.3, y: 0.54 }, { x: 0.5, y: 0.54 }, { x: 0.73, y: 0.48 }],
    targets: [{ x: 0.65, y: 0.48 }, { x: 0.42, y: 0.58 }, { x: 0.89, y: 0.24 }],
    routes: ["exchange", "drift", "lift"],
    defenderGap: [0.14, 0.3, 0.12],
    reactions: [16, 14, 21],
    secondPassRoute: { periods: [163, 174, 154], phases: [52, 61, 11] },
  },
  {
    label: "Baseline brush",
    difficulty: "mixed",
    ballhandler: "o2",
    points: [{ x: 0.16, y: 0.31 }, { x: 0.5, y: 0.62 }, { x: 0.7, y: 0.48 }],
    targets: [{ x: 0.38, y: 0.27 }, { x: 0.72, y: 0.56 }, { x: 0.88, y: 0.23 }],
    routes: ["shallow-cut", "exchange", "lift"],
    defenderGap: [0.15, 0.2, 0.3],
    reactions: [20, 16, 18],
  },
  {
    label: "Late relocation",
    difficulty: "scramble",
    ballhandler: "o0",
    points: [{ x: 0.48, y: 0.54 }, { x: 0.19, y: 0.45 }, { x: 0.81, y: 0.42 }],
    targets: [{ x: 0.35, y: 0.52 }, { x: 0.1, y: 0.23 }, { x: 0.65, y: 0.39 }],
    routes: ["drift", "lift", "relocate"],
    defenderGap: [0.3, 0.13, 0.17],
    reactions: [14, 19, 15],
  },
  {
    label: "Beat the horn",
    difficulty: "climax",
    ballhandler: "o1",
    points: [{ x: 0.23, y: 0.51 }, { x: 0.51, y: 0.54 }, { x: 0.79, y: 0.44 }],
    targets: [{ x: 0.12, y: 0.25 }, { x: 0.63, y: 0.56 }, { x: 0.9, y: 0.22 }],
    routes: ["lift", "exchange", "lift"],
    defenderGap: [0.13, 0.28, 0.11],
    reactions: [19, 13, 20],
    climax: true,
  },
];

function parseUtcDate(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`Invalid UTC date: ${value}`);
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.valueOf()) || parsed.toISOString().slice(0, 10) !== value) {
    throw new Error(`Invalid UTC date: ${value}`);
  }
  return parsed;
}

function daysBetween(start: string, end: string): number {
  return Math.floor((parseUtcDate(end).valueOf() - parseUtcDate(start).valueOf()) / 86_400_000);
}

function mirror(point: Vec2): Vec2 {
  return { x: 1 - point.x, y: point.y };
}

function makeOffense(
  template: Template,
  random: SeededRandom,
  mirrored: boolean,
  generatorVersion: number,
): OffensivePlayerSpec[] {
  return template.points.map((point, index) => {
    const target = template.targets[index];
    const jitterX = random.range(-0.018, 0.018);
    const jitterY = random.range(-0.012, 0.012);
    const origin = {
      x: clamp(point.x + jitterX, 0.075, 0.925),
      y: clamp(point.y + jitterY, 0.12, 0.9),
    };
    const destination = {
      x: clamp(target.x + jitterX * 0.5, 0.075, 0.925),
      y: clamp(target.y + jitterY * 0.5, 0.12, 0.9),
    };
    const randomPeriod = random.integer(150, 220);
    const randomPhase = generatorVersion >= 2 ? random.integer(0, 72) : random.integer(0, 28);
    return {
      id: `o${index}` as PlayerId,
      route: {
        kind: template.routes[index],
        origin: mirrored ? mirror(origin) : origin,
        target: mirrored ? mirror(destination) : destination,
        periodTicks: generatorVersion >= 2 && template.secondPassRoute
          ? template.secondPassRoute.periods[index]
          : randomPeriod,
        phaseTicks: generatorVersion >= 2 && template.secondPassRoute
          ? template.secondPassRoute.phases[index]
          : randomPhase,
        sway: random.range(0.006, 0.018),
      },
    };
  });
}

function makePossession(
  template: Template,
  index: number,
  seed: number,
  generatorVersion: number,
): PossessionSpec {
  const random = new SeededRandom(hashString(`${seed}:possession:${index}`));
  const mirrored = random.next() > 0.5;
  const offense = makeOffense(template, random, mirrored, generatorVersion);
  const defense = offense.map((player, defenderIndex) => {
    const gap = template.defenderGap[defenderIndex];
    const side = player.route.origin.x < 0.5 ? 1 : -1;
    const difficultyHelp = template.difficulty === "flow"
      ? [0.76, 0.88] as const
      : template.difficulty === "mixed"
        ? [0.66, 0.82] as const
        : [0.74, 0.89] as const;
    return {
      id: `d${defenderIndex}` as DefenderId,
      assignment: player.id,
      start: {
        x: clamp(player.route.origin.x + side * gap * 0.36, 0.08, 0.92),
        y: clamp(player.route.origin.y - gap, 0.1, 0.86),
      },
      reactionTicks: template.reactions[defenderIndex] + random.integer(-2, 2),
      speedPerTick: generatorVersion >= 2
        ? random.range(0.00375, 0.00445)
        : random.range(0.0028, 0.004),
      armLength: random.range(0.09, 0.108),
      ...(generatorVersion >= 2 ? {
        accelerationPerTick: random.range(0.0005, 0.0008),
        helpCommitment: random.range(difficultyHelp[0], difficultyHelp[1]),
        stuntTicks: random.integer(14, template.difficulty === "flow" ? 20 : 25),
      } : {}),
    };
  });
  return {
    id: `${seed.toString(16)}-${index + 1}`,
    index,
    label: template.label,
    difficulty: template.difficulty,
    ballhandler: template.ballhandler,
    offense,
    defense,
    noviceFloor: template.noviceFloor === true,
    climax: template.climax === true,
    secondPassFocus: generatorVersion >= 2 && template.secondPassRoute !== undefined,
  };
}

export function createDailyCourt(options?: DailyCourtOptions): DailyCourt;
export function createDailyCourt(utcDate: string, options?: Omit<DailyCourtOptions, "utcDate">): DailyCourt;
export function createDailyCourt(
  input: string | DailyCourtOptions = {},
  extra: Omit<DailyCourtOptions, "utcDate"> = {},
): DailyCourt {
  const options = typeof input === "string" ? { ...extra, utcDate: input } : input;
  const utcDate = options.utcDate ?? new Date().toISOString().slice(0, 10);
  parseUtcDate(utcDate);
  const count = options.count ?? POSSESSION_COUNT;
  if (!Number.isInteger(count) || count < 1 || count > POSSESSION_COUNT) {
    throw new Error(`Possession count must be an integer from 1 to ${POSSESSION_COUNT}`);
  }
  // Never change the shared court underneath players during a UTC day. V2 was
  // shipped on August 18 but activates with puzzle #2 at the next rollover.
  const generatorVersion = options.generatorVersion
    ?? (utcDate >= V2_START_UTC_DATE ? 2 : 1);
  const launchEpoch = options.launchEpoch ?? DEFAULT_LAUNCH_EPOCH;
  const puzzleNumber = daysBetween(launchEpoch, utcDate) + 1;
  const seed = hashString(`${generatorVersion}:${utcDate}:gameplay`);
  const dayRandom = new SeededRandom(seed);
  const profile = (["flow", "mixed", "scramble"] as const)[dayRandom.integer(0, 2)];

  // The first three are the polished slice. For a full court the climax is always
  // included as possession eight; middle templates rotate by day without changing rules.
  const middle = [...TEMPLATES.slice(1, -1)];
  if (generatorVersion >= 2) dayRandom.shuffle(middle);
  const selected = count === POSSESSION_COUNT
    ? [TEMPLATES[0], ...middle, TEMPLATES.at(-1)!]
    : TEMPLATES.slice(0, count);
  const possessions = selected.map((template, index) => makePossession(template, index, seed, generatorVersion));

  return { generatorVersion, utcDate, puzzleNumber, seed, profile, possessions };
}
